import { Router } from "@angular/router";

export const PAYMENTS_PAYMENT_LINKS_TAB_QUERY = "payment-links";

/** 0=Pagamenti, 1=Payment Links, 2=Bonifici, 3=Terminal, 4=Methods, 5=Settings */
export const PAYMENTS_PAYMENTS_TAB_INDEX = 0;
export const PAYMENTS_PAYMENT_LINKS_TAB_INDEX = 1;
export const PAYMENTS_PAYOUTS_TAB_INDEX = 2;
export const PAYMENTS_TERMINAL_TAB_INDEX = 3;
export const PAYMENTS_SETTINGS_TAB_INDEX = 5;

export type PaymentsHeaderAction = {
  id: string;
  name: string;
  icon: string;
};

export const PAYMENTS_CONNECTION_CODES_ACTION: PaymentsHeaderAction = {
  id: "connection-codes",
  name: "payments.connectionCodes.openButton",
  icon: "link",
};

export const PAYMENTS_VIEW_SWITCH_TO_CLASSIC: PaymentsHeaderAction = {
  id: "switch-classic-view",
  name: "payments.action.switchToClassicView",
  icon: "history",
};

export const PAYMENTS_VIEW_SWITCH_TO_NEW: PaymentsHeaderAction = {
  id: "switch-new-view",
  name: "payments.action.switchToNewView",
  icon: "auto_awesome",
};

export function buildPaymentsHeaderMoreActions(useBetaView: boolean): PaymentsHeaderAction[] {
  return [
    PAYMENTS_CONNECTION_CODES_ACTION,
    useBetaView ? PAYMENTS_VIEW_SWITCH_TO_CLASSIC : PAYMENTS_VIEW_SWITCH_TO_NEW,
  ];
}

const PAYMENTS_BETA_VIEW_PREF_KEY_PREFIX = "ws-payments-use-beta-view";

export function paymentsBetaViewPreferenceKey(wineryId: string | number | undefined | null): string | null {
  if (wineryId == null || wineryId === "") {
    return null;
  }
  return `${PAYMENTS_BETA_VIEW_PREF_KEY_PREFIX}:${wineryId}`;
}

/** Saved choice: Beta vs previous (Stripe) lists — scoped per winery. */
export function readPaymentsBetaViewPreference(wineryId: string | number | undefined | null): boolean | null {
  const key = paymentsBetaViewPreferenceKey(wineryId);
  if (!key) {
    return null;
  }
  try {
    const raw = localStorage.getItem(key);
    if (raw === "1" || raw === "true") {
      return true;
    }
    if (raw === "0" || raw === "false") {
      return false;
    }
  } catch {
    // ignore storage errors
  }
  return null;
}

export function writePaymentsBetaViewPreference(
  wineryId: string | number | undefined | null,
  useBetaView: boolean,
): void {
  const key = paymentsBetaViewPreferenceKey(wineryId);
  if (!key) {
    return;
  }
  try {
    localStorage.setItem(key, useBetaView ? "1" : "0");
  } catch {
    // ignore storage errors
  }
}

export function navigateToPaymentsPaymentLinksTab(router: Router): void {
  router.navigate(["/payments"], { queryParams: { tab: PAYMENTS_PAYMENT_LINKS_TAB_QUERY } });
}

export type PaymentBrand = "klarna" | "satispay" | "paypal";

export const PAYMENT_BRAND_ICONS: { [key: string]: string } = {
  klarna: "assets/images/payments/klarna.svg",
  satispay: "assets/images/payments/satispay.svg",
  paypal: "assets/images/payments/paypal.svg",
};

const BRAND_LABEL_KEYS: { [key: string]: string } = {
  klarna: "payments.dwspay.methodKlarna",
  satispay: "payments.dwspay.methodSatispay",
  paypal: "payments.dwspay.methodPaypal",
};

export function normalizePaymentBrand(raw?: string | null): PaymentBrand | null {
  const value = String(raw || "").toLowerCase().replace(/[\s-]+/g, "_");
  if (!value) {
    return null;
  }
  if (value.indexOf("klarna") >= 0) {
    return "klarna";
  }
  if (value.indexOf("satispay") >= 0) {
    return "satispay";
  }
  if (value.indexOf("paypal") >= 0) {
    return "paypal";
  }
  return null;
}

export function paymentBrandIcon(raw?: string | null): string {
  const brand = normalizePaymentBrand(raw);
  return brand ? PAYMENT_BRAND_ICONS[brand] || "" : "";
}

export function paymentBrandLabelKey(raw?: string | null): string | null {
  const brand = normalizePaymentBrand(raw);
  return brand ? BRAND_LABEL_KEYS[brand] : null;
}

/** Brand actually charged. Charge details win over the allowed-types list. */
export function paymentBrandFromIntent(intent: any): PaymentBrand | null {
  if (!intent || typeof intent !== "object") {
    return null;
  }
  const charge = resolveCharge(intent);
  const fromCharge = normalizePaymentBrand(charge && charge.payment_method_details && charge.payment_method_details.type);
  if (fromCharge) {
    return fromCharge;
  }
  const method = intent.payment_method;
  if (method && typeof method === "object") {
    const fromMethod = normalizePaymentBrand(method.type);
    if (fromMethod) {
      return fromMethod;
    }
  }
  const types = intent.payment_method_types;
  if (Array.isArray(types) && types.length === 1) {
    return normalizePaymentBrand(types[0]);
  }
  return null;
}

function resolveCharge(intent: any): any {
  const snapshot = intent.charge_succeeded_snapshot;
  if (snapshot && typeof snapshot === "object" && !Array.isArray(snapshot)) {
    return snapshot;
  }
  const latest = intent.latest_charge || intent.latestCharge;
  if (latest && typeof latest === "object" && !Array.isArray(latest)) {
    return latest;
  }
  const charges = intent.charges;
  const first = charges && charges.data && charges.data[0];
  if (first && typeof first === "object") {
    return first;
  }
  return null;
}
